import { useState, useMemo } from 'react';
import { CartItem, Product } from '../types';

const CONVENIENCE_FEE_RATE = 0.03;

export function useCart() {
  const [items, setItems] = useState<CartItem[]>([]);
  const [tip, setTip] = useState(0);

  const addItem = (product: Product, quantity = 1) => {
    setItems((current) => {
      const existingIndex = current.findIndex((item) => item.product_id === product.id);

      if (existingIndex >= 0) {
        // Update quantity of existing item
        const updated = [...current];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity,
        };
        return updated;
      } else {
        // Add new item
        return [
          ...current,
          {
            product_id: product.id,
            name: product.name,
            price: product.price,
            quantity,
            tax_rate: product.tax_rate,
          },
        ];
      }
    });
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }

    setItems((current) =>
      current.map((item) =>
        item.product_id === productId ? { ...item, quantity } : item
      )
    );
  };

  const removeItem = (productId: string) => {
    setItems((current) => current.filter((item) => item.product_id !== productId));
  };

  const clearCart = () => {
    setItems([]);
    setTip(0);
  };

  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const tax = items.reduce((sum, item) => {
      const itemSubtotal = item.price * item.quantity;
      return sum + Math.round(itemSubtotal * (item.tax_rate / 100));
    }, 0);
    const convenienceFee = Math.round(subtotal * CONVENIENCE_FEE_RATE);
    const total = subtotal + tax + convenienceFee + tip;

    return {
      subtotal,
      tax,
      convenienceFee,
      tip,
      total,
    };
  }, [items, tip]);

  return {
    items,
    tip,
    setTip,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    totals,
  };
}
